import { createFileRoute } from "@tanstack/react-router";
import { Sparkles, MapPin, MessageSquare, Phone, HelpCircle, Megaphone } from "lucide-react";
import { PageContentEditor, type PageSectionDef } from "@/components/admin/PageContentEditor";
import { getAdminContactPage, updateAdminContactPage } from "@/lib/admin/pages.functions";

export const Route = createFileRoute("/_admin/admin/contact")({
  component: AdminContactPage,
});

const SECTIONS: PageSectionDef[] = [
  {
    title: "Page header",
    icon: Sparkles,
    description: "The eyebrow, headline, intro and banner shown at the top of the Contact page.",
    fields: [
      { key: "hero_eyebrow", label: "Eyebrow", help: "Small label above the headline." },
      { key: "hero_title", label: "Headline", help: "Main page headline." },
      { key: "hero_intro", label: "Intro", help: "Short paragraph under the headline.", type: "textarea" },
      { key: "hero_image_url", label: "Hero image", help: "Wide banner directly under the page header.", type: "image" },
      { key: "hero_image_caption", label: "Hero image caption", help: "Caption shown under the hero banner." },
    ],
  },
  {
    title: "Contact information",
    icon: Phone,
    description: "The 'how to reach us' block with phone, WhatsApp and service area.",
    fields: [
      { key: "info_eyebrow", label: "Eyebrow", help: "Small label above the contact heading." },
      { key: "info_heading", label: "Contact heading", help: "Heading of the contact information block." },
      { key: "info_description", label: "Contact description", help: "Short paragraph about response times and availability.", type: "textarea" },
      { key: "office_image_url", label: "Office / kitchen image", help: "Photo shown with the contact details.", type: "image" },
      { key: "office_image_caption", label: "Office image caption", help: "Caption shown under the office photo." },
    ],
  },
  {
    title: "Enquiry form",
    icon: MessageSquare,
    description: "The contact form block and the artwork shown beside it.",
    fields: [
      { key: "form_eyebrow", label: "Eyebrow", help: "Small label above the form heading." },
      { key: "form_heading", label: "Form heading", help: "Heading beside the enquiry form." },
      { key: "form_description", label: "Form description", help: "Short paragraph explaining what happens after submitting.", type: "textarea" },
      { key: "illustration_image_url", label: "Illustration", help: "Supporting illustration beside the enquiry form.", type: "image" },
      { key: "illustration_image_caption", label: "Illustration caption", help: "Caption shown under the illustration." },
    ],
  },
  {
    title: "Delivery area",
    icon: MapPin,
    description: "The delivery-area block with the map graphic.",
    fields: [
      { key: "delivery_heading", label: "Delivery heading", help: "Heading of the delivery-area block." },
      { key: "delivery_description", label: "Delivery description", help: "Short paragraph about the neighbourhoods you serve.", type: "textarea" },
      { key: "map_image_url", label: "Map image", help: "Static map or service-area graphic.", type: "image" },
      { key: "map_image_caption", label: "Map image caption", help: "Caption shown under the map." },
    ],
  },
  {
    title: "Frequently asked questions",
    icon: HelpCircle,
    description: "The heading and intro above the FAQ accordion.",
    fields: [
      { key: "faq_heading", label: "FAQ heading", help: "Heading above the FAQ list." },
      { key: "faq_intro", label: "FAQ intro", help: "Short paragraph introducing the FAQ list.", type: "textarea" },
    ],
  },
  {
    title: "Closing call to action",
    icon: Megaphone,
    description: "The closing banner and buttons just above the footer.",
    fields: [
      { key: "cta_eyebrow", label: "Eyebrow", help: "Small label above the closing headline." },
      { key: "cta_heading", label: "Headline", help: "Closing call-to-action headline." },
      { key: "cta_description", label: "Description", help: "Supporting sentence under the headline.", type: "textarea" },
      { key: "cta_primary_text", label: "Primary button text", help: "Label of the main button." },
      { key: "cta_primary_link", label: "Primary button link", help: "Internal path such as /meal-plans, or an https:// URL." },
      { key: "cta_secondary_text", label: "Secondary button text", help: "Label of the second button." },
      { key: "cta_secondary_link", label: "Secondary button link", help: "Internal path such as /contact, or an https:// URL." },
      { key: "cta_image_url", label: "Closing banner", help: "Image above the closing call to action.", type: "image" },
      { key: "cta_image_caption", label: "Closing banner caption", help: "Caption shown under the closing banner." },
    ],
  },
];

function AdminContactPage() {
  return (
    <PageContentEditor
      title="Contact Page"
      description="Edit every section of the Contact page. Text and imagery live together, and images are picked from the Media Library."
      sections={SECTIONS}
      queryKey="admin-contact-content"
      load={getAdminContactPage as never}
      save={updateAdminContactPage as never}
    />
  );
}
